import { QtyPerType } from '@snap-types/models/enums';
import { SnapRequirement as BaseSnapRequirement } from '@snap-types/models/snap-requirement';
import { Decimal } from '@snap-types/types/decimal';

export type SnapRequirementCostParams = Pick<
  BaseSnapRequirement,
  | 'qtyPer'
  | 'qtyPerType'
  | 'fixedQty'
  | 'unitMaterialCost'
  | 'unitLaborCost'
  | 'unitBurdenCost'
  | 'unitServiceCost'
  | 'fixedCost'
  | 'scrapPercent'
  | 'pieceNo'
  | 'partUuid'
>;

export type SnapRequirementCostResults = {
  burdenCost: Decimal;
  laborCost: Decimal;
  materialCost: Decimal;
  quantity: Decimal;
  serviceCost: Decimal;
};

export function calculateRequirementCosts(
  {
    qtyPer,
    qtyPerType,
    fixedQty,
    unitMaterialCost,
    unitLaborCost,
    unitBurdenCost,
    unitServiceCost,
    fixedCost,
    scrapPercent,
  }: SnapRequirementCostParams,
  operationStartQuantity: Decimal,
  operationEndQuantity: Decimal,
): SnapRequirementCostResults {
  /** The input quantity of material for the operation associated with this requirement.
   * This quantity is calculated from the output quantity of
   * the operation and the operation's scrap values.
   * In Visual, this corresponds to the CALC_START_QTY field in the OPERATION table.*/

  // material is calculated on the operation's "start" or "end" quantity
  //  depending on the value of `qtyPerType`
  const operationQuantity =
    qtyPerType === QtyPerType.START
      ? operationStartQuantity
      : operationEndQuantity;

  // add back the % scrap for total material
  const scrapMultiplier = new Decimal(1).plus(scrapPercent.dividedBy(100));

  const quantity = fixedQty.plus(
    qtyPer.times(operationQuantity).times(scrapMultiplier),
  );

  const materialCost = quantity
    .times(unitMaterialCost)
    .plus(fixedCost ?? new Decimal(0));

  const laborCost = quantity.times(unitLaborCost);

  const burdenCost = quantity.times(unitBurdenCost);

  const serviceCost = quantity.times(unitServiceCost);

  return {
    quantity,
    materialCost,
    laborCost,
    burdenCost,
    serviceCost,
  };
}
